import { IconButton } from '@ui/shared/Button';
import sortBy from 'lodash/sortBy';
import * as React from 'react';
import styled from 'styled-components';
import {
  IQuadrantReportState,
  IQuadrantResult,
} from '../../../data/providers/reports/quadrantReportProvider';
import { Entity } from '../../../data/types';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import { CardHeader } from '../../shared/card/CardHeader';
import { ListHeader } from '../../shared/List/ListHeader';
import { EPdfMode } from '../../shared/pdf/Pdf';
import { QuadrantResult } from './QuadrantResult';

interface IProps {
  reportState: IQuadrantReportState;
  colors: string[];
  pdfMode: EPdfMode;
  entity: Entity;
}

interface ISort {
  key: 'x' | 'y' | 'rank';
  direction: 'asc' | 'desc';
}

export const ResultList = (props: IProps): React.ReactElement => {
  const [sort, setSort] = React.useState<ISort>();

  const data = React.useMemo((): IQuadrantResult[] => {
    if (!sort) {
      return props.reportState.quadrantResults;
    }
    return sortBy(
      props.reportState.quadrantResults,
      (item: IQuadrantResult): number => {
        if (sort?.key === 'rank') {
          return item.rank * (sort.direction === 'desc' ? -1 : 1);
        } else {
          return item[sort.key].value * (sort.direction === 'desc' ? -1 : 1);
        }
      }
    );
  }, [JSON.stringify(props.reportState.quadrantResults), JSON.stringify(sort)]);

  const onXSort = (): void => {
    const direction = sort?.direction === 'asc' ? 'desc' : 'asc';
    setSort({ key: 'x', direction });
  };

  const onYSort = (): void => {
    const direction = sort?.direction === 'asc' ? 'desc' : 'asc';
    setSort({ key: 'y', direction });
  };

  const onRankSort = (): void => {
    const direction = sort?.direction === 'desc' ? 'asc' : 'desc';
    setSort({ key: 'rank', direction });
  };

  const listShape = [0.5, 1, 1, 1, 1];
  return (
    <Row>
      <Container>
        <Card
          style={{ marginTop: 'var(--grid-gap)' }}
          inverse={props.pdfMode === EPdfMode.PRINT}
        >
          <Column>
            <CardHeader surface={24} withListHeader>
              <Column>
                <ListHeader
                  leftAlignLastCell
                  shape={listShape}
                  labels={[
                    <SortableLabel
                      key={0}
                      sort={sort}
                      onSort={onRankSort}
                      label='Rank'
                    />,
                    'Athlete',
                    <MetricLabel
                      key={2}
                      testMeta={props.reportState.xAxisTestMeta}
                      metricLabel={props.reportState.xAxisMetricLabel}
                      onSort={onXSort}
                      sort={sort}
                    />,
                    <MetricLabel
                      key={3}
                      testMeta={props.reportState.yAxisTestMeta}
                      metricLabel={props.reportState.yAxisMetricLabel}
                      onSort={onYSort}
                      sort={sort}
                    />,
                    'Category',
                  ]}
                />
              </Column>
            </CardHeader>
            <CardBody>
              {data.map(
                (
                  result: IQuadrantResult,
                  index: number
                ): React.ReactElement => {
                  return (
                    <QuadrantResult
                      result={result}
                      key={index}
                      color={props.colors[index % 2]}
                      pdfMode={props.pdfMode}
                      entity={props.entity}
                      rank={result.rank}
                      shape={listShape}
                    />
                  );
                }
              )}
            </CardBody>
          </Column>
        </Card>
      </Container>
    </Row>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: var(--grid-gap);
`;

interface IMetricLabelProps {
  testMeta: string;
  metricLabel: string;
  sort: ISort;
  onSort: () => void;
}

interface ISortableLabelProps {
  sort: ISort;
  label: string;
  onSort: () => void;
}
const SortableLabel = (props: ISortableLabelProps): React.ReactElement => {
  return (
    <Row>
      <div style={{ flex: 1 }}>
        <div>{props.label}</div>
      </div>
      <div style={{ flex: 0, marginLeft: 'auto', paddingRight: '10px' }}>
        <IconButton
          title={props.sort?.direction === 'asc' ? 'Sort Desc' : 'Sort Asc'}
          icon={props.sort?.direction === 'asc' ? 'expand_more' : 'expand_less'}
          onClick={props.onSort}
        />
      </div>
    </Row>
  );
};

const MetricLabel = (props: IMetricLabelProps): React.ReactElement => {
  return (
    <Row>
      <div style={{ flex: 1 }}>
        <div>{props.testMeta}</div>
        <div>{props.metricLabel}</div>
      </div>
      <div style={{ flex: 0, marginLeft: 'auto', paddingRight: '10px' }}>
        <IconButton
          title={props.sort?.direction === 'asc' ? 'Sort Desc' : 'Sort Asc'}
          icon={props.sort?.direction === 'asc' ? 'expand_more' : 'expand_less'}
          onClick={props.onSort}
        />
      </div>
    </Row>
  );
};
